// The view lives in the URL, so a link or a reload brings back the same scene: scene, view, the
// scene's choices (run, drug, model), selection, isolation, section, hand-made layers, time and
// playback, the Stroke and Blood flow controls (clot, collaterals, thrombectomy, Compare, Colour by,
// particles), the Dose slider, the receptor and montage the traces show, the electrode switches,
// labels, explode and presentation. Written with replaceState (no history spam); read once at start.
// Only what differs from the scene's own state is written, so the default view is an empty query.
//
// `shareUrl` adds what the bar never carries: `cam`, the exact camera. A pack load re-sends the view's
// own camera (viewport/bridge.ts syncAll), so a URL camera is applied once the load has replied.

import { useAtlas, NO_PARTICLES, type AtlasState, type LabelDensity, type StrokeState } from './store.ts';
import { DEFAULT_CURRENT, DEFAULT_SHOWN_CURRENT_MA, INTENSITY, useStim } from '../features/stim.ts';
import { DEFAULT_CONDITION, useDfc } from '../features/dfc.ts';
import { useCompare } from '../features/compare.ts';
import { useDrugMaps } from '../features/drugMaps.ts';
import { requestSource, useMindLive } from '../features/mindLive.ts';
import { DEFAULT_QUERY, useText } from '../features/text.ts';
import { DEFAULT_TRACERS, DEFAULT_WALL, isWall, useCfd } from '../features/cfd.ts';
import { DEFAULT_BEYOND, DEFAULT_GROWTH, DEFAULT_MAP, DEFAULT_METRIC, DEFAULT_PD_TRACK, DEFAULT_STAGE, DEFAULT_TH_AGE, DEFAULT_TH_SEX, DEFAULT_THICKNESS, DEFAULT_TRACK, GROWTH_RANGE, isBeyond, isMap, isPdTrack, isSex, isTrack, metricFromParam, metricParam, STAGE_RANGE, THICKNESS_AGES, useLifetime } from '../features/lifetime.ts';
import { decodeTour, encodeTour } from '../features/tours.ts';
import { tourTo } from '../features/Tour.tsx';
import { useJourney } from '../features/journey.ts';
import { journeyOf, sceneTimelineOf } from './scene.ts';
import { PD_VIEW, scenarioById, SCENARIOS, THICKNESS_VIEW, type Scenario, type ScenarioId } from '../scenarios/index.ts';
import { siteOf } from './stroke.ts';
import type { Mode, Orbit, SectionPlane, Target } from '../viewport/api.ts';
import { isShow, useDrugDensity } from '../features/drugDensity.ts';
import { lastCamera } from '../viewport/events.ts';

/** Query keys that carry no scene state: a tour link with only these opens its step's own state (tourTo). */
const BARE_TOUR_KEYS = new Set(['tour', 'play', 'speed', 'present', 'engine']);

const AXES = new Set(['axial', 'coronal', 'sagittal']);
const MODES = new Set<Mode>(['shown', 'ghost', 'hidden']);
const DENSITIES = new Set<LabelDensity>(['few', 'normal', 'many']);
const COLOURS = new Set<StrokeState['colourBy']>(['velocity', 'pressure', 'none']);
const THROMBECTOMY_H = new Set([1, 3, 6]);
const MONTAGES = new Set(['eeg', 'seeg']);

/** Developer switches that survive a state change in the bar, and are left out of a shared link. */
export const DEV_PARAMS = ['engine', 'stats', 'mockApi'];

export type UrlState = Pick<
  AtlasState,
  'scenario' | 'preset' | 'selected' | 'isolate' | 'section' | 'time' | 'presenting' | 'choices' | 'sims' | 'stroke' | 'particles' | 'drugDose' | 'traceView' | 'toggles' | 'labelsOn' | 'labelDensity' | 'explode' | 'layers' | 'overrides' | 'tracesOpen' | 'playing' | 'galleryOpen' | 'galleryItem' | 'tour'
>;

function encodeTarget(t: Target | null): string | null {
  if (!t) return null;
  if ('id' in t) return t.id;
  if ('system' in t) return `system:${t.system}`;
  return null;
}

function decodeTarget(v: string | null): Target | null {
  if (!v) return null;
  if (v.startsWith('system:')) return { system: v.slice(7) };
  return { id: v };
}

/** A number as short as its precision allows: 0.300 → 0.3, 9300 → 9300. */
const num = (v: number, digits: number) => v.toFixed(digits).replace(/\.?0+$/, '');

/** The scene's choices a link can set: its own controls, not what the Stroke slice derives. */
const linkChoices = (sc: Scenario) => (sc.choices ?? []).filter((c) => !c.fromSlice);

/** A chosen run as its index id where the scene's options know it, else as chosen (hemo's `v5`). */
function choiceId(s: Pick<UrlState, 'scenario' | 'sims'>, id: string, value: string): string {
  const opts = s.sims.scenario === s.scenario ? (s.sims.options[id] as { value: string; id?: string }[] | undefined) : undefined;
  return opts?.find((o) => o.value === value)?.id ?? value;
}

/** The scene's clot before anyone moves it: Stroke opens with one, Blood flow with none. */
const defaultClot = (sc: Scenario) => sc.live?.clot ?? null;

export function toQuery(s: UrlState): string {
  const q = new URLSearchParams();
  const sc = scenarioById(s.scenario);
  if (s.scenario !== 'explore') q.set('scene', s.scenario);
  const view = sc.presets.find((p) => p.id === s.preset);
  // A journey view's link names the journey (docs/areas/web-powers-journey.md): `journey=powers-of-ten`, not the preset.
  if (view?.journey) q.set('journey', view.journey.id);
  else if (s.preset && s.preset !== sc.presets[0]?.id) q.set('view', s.preset);
  for (const c of linkChoices(sc)) {
    const v = s.choices[c.id];
    if (v) q.set(c.id, choiceId(s, c.id, v));
  }
  if (s.selected) q.set('select', s.selected);
  const iso = encodeTarget(s.isolate);
  if (iso && !s.preset) q.set('isolate', iso);
  const plane = s.section[0];
  if (plane && !s.preset) q.set('section', `${plane.axis}:${Math.round(plane.mm)}`);
  if (!s.preset) {
    // Hand-made visibility: a view's own layers come back with the view.
    const layers = Object.entries(s.layers).filter(([, m]) => MODES.has(m));
    if (layers.length) q.set('layers', layers.map(([sys, m]) => `${sys}:${m}`).join(','));
    for (const mode of ['hidden', 'ghost'] as const) {
      const ids = Object.entries(s.overrides)
        .filter(([, m]) => m === mode)
        .map(([id]) => id);
      if (ids.length) q.set(mode === 'hidden' ? 'hide' : 'ghost', ids.join(','));
    }
  }
  if (s.time > 0) q.set('t', num(s.time, 3));
  if (s.playing) q.set('play', '1');
  // The clot and the clinical controls (spec §15), where the scene has them.
  if (sc.live) {
    const o = s.stroke.occlusion;
    const d = defaultClot(sc);
    if (!o && d) q.set('clot', 'none');
    else if (o && (!d || o.vessel !== d.vessel || Math.abs(o.s - d.s) > 0.005)) q.set('clot', `${o.vessel}@${num(o.s, 2)}`);
    if (!s.stroke.collaterals) q.set('collaterals', '0');
    else if (s.stroke.fair) q.set('collaterals', 'fair');
    if (s.stroke.pcom === 'traced' && siteOf(o?.vessel) === 'basilar') q.set('pcom', 'traced');
    if (s.stroke.thrombectomyH) q.set('thrombectomy', String(s.stroke.thrombectomyH));
    if (s.stroke.compare) q.set('compare', '1');
    if (s.stroke.colourBy !== 'velocity') q.set('colour', s.stroke.colourBy);
    if (!s.particles.on) q.set('particles', 'off');
    else if (Math.abs(s.particles.density - NO_PARTICLES.density) > 0.001) q.set('particles', num(s.particles.density, 2));
  }
  if (s.drugDose !== null) q.set('dose', num(s.drugDose, 4));
  // Drugs' Show control (features/drugDensity.ts): the receptor's density or PET map in the engagement's place.
  if (s.scenario === 'drugs' && useDrugDensity.getState().show !== 'engagement') q.set('show', useDrugDensity.getState().show);
  // Drugs' Compare sheet (features/compare.ts), open.
  if (s.scenario === 'drugs' && useCompare.getState().open) q.set('compare', '1');
  // Drugs' All maps sheet (features/drugMaps.ts), open.
  if (s.scenario === 'drugs' && useDrugMaps.getState().open) q.set('maps', '1');
  if (s.traceView.target) q.set('target', s.traceView.target);
  if (s.traceView.montage) q.set('montage', s.traceView.montage);
  // The electrode switches, against what the view sets; the model's activity and a patient's contacts are on by default.
  for (const k of ['eeg', 'seeg'] as const) {
    const own = view?.electrodes?.[k] ?? false;
    if (s.toggles[k] !== own) q.set(k, s.toggles[k] ? '1' : '0');
  }
  if (!s.toggles.activity) q.set('activity', '0');
  if (!s.toggles.contacts) q.set('contacts', '0');
  if (!s.labelsOn) q.set('labels', '0');
  if (s.labelDensity !== 'normal') q.set('density', s.labelDensity);
  if (s.explode > 0) q.set('explode', num(s.explode, 2));
  if (!s.tracesOpen) q.set('traces', '0');
  if (s.presenting) q.set('present', '1');
  // The Gallery sheet, and the card it is open on: `gallery=1`, or `gallery=<card id>`.
  if (s.galleryOpen) q.set('gallery', s.galleryItem ?? '1');
  // The guided tour and its step, `tour=start.3` (features/tours.ts).
  if (s.tour) q.set('tour', encodeTour(s.tour));
  // Stimulate's coil current, MA/s, where it isn't the run's own 1.
  if (s.scenario === 'stim') {
    const st = useStim.getState();
    if (st.intensity !== 1) q.set('intensity', num(st.intensity, 2));
    // A tDCS montage's current, mA, when it isn't the FEM's own (the control's reference).
    if (st.current !== DEFAULT_SHOWN_CURRENT_MA) q.set('current', num(st.current, 1));
    if (st.offset[0] !== 0 || st.offset[1] !== 0) q.set('coil', `${num(st.offset[0], 1)},${num(st.offset[1], 1)}`);
    // The coil's Source model, where it's on (design's default is off): `source=1`.
    if (st.sourceModel) q.set('source', '1');
  }
  // The measured ΔFC view's condition (features/dfc.ts), where it isn't the default: `condition=lsd-allrest-nogsr`.
  if (s.scenario === 'drugs' && s.preset === 'dfc') {
    const d = useDfc.getState();
    if (d.condition !== DEFAULT_CONDITION) q.set('condition', d.condition);
    if (d.matrixOpen) q.set('matrix', '1');
  }
  // Mind › Vision's drawing source (features/mindLive.ts): `live=paint` while Paint is on or a link has asked for it.
  if (s.scenario === 'mind') {
    const m = useMindLive.getState();
    if ((m.kind === 'paint' && m.status !== 'error') || m.wanted === 'paint') q.set('live', 'paint');
  }
  // Mind’s Words into maps: the topic, where it isn’t the view’s own (features/text.ts): `q=pain`.
  if (s.scenario === 'mind' && s.preset === 'text') {
    const t = useText.getState().query;
    if (t !== DEFAULT_QUERY) q.set('q', t);
  }
  // Blood flow’s 3D flow (features/cfd.ts): the wall’s field where it isn’t shear now, `wall=tawss`; the tracers where they
  // aren’t the 20,000 drawn by default, `tracers=10000`, or off, `tracers=0`.
  if (s.scenario === 'bloodflow' && s.preset === 'cfd') {
    const c = useCfd.getState();
    if (c.wall !== DEFAULT_WALL) q.set('wall', c.wall);
    if (!c.tracersOn) q.set('tracers', '0');
    else if (c.tracers !== DEFAULT_TRACERS) q.set('tracers', String(c.tracers));
  }
  // Lifetime (features/lifetime.ts): what the surface is lit by, where it isn’t plain: `map=depth`; the track where it's the
  // model's, `track=model`.
  if (s.scenario === 'lifetime') {
    const l = useLifetime.getState();
    if (l.map !== DEFAULT_MAP) q.set('map', l.map);
    if (l.track !== DEFAULT_TRACK) q.set('track', l.track);
    // Assigned thickness (LIFE-020), on that track: the imposed growth (`g=2.25`, the track's own axis, never the scene's `t`), the
    // thickness (`h=3`) and whether a track's last accepted state shows past its range (`beyond=last`), where they aren't its defaults.
    if (l.track === 'assigned') {
      if (l.growth !== DEFAULT_GROWTH) q.set('g', num(l.growth, 6));
      if (l.thickness !== DEFAULT_THICKNESS) q.set('h', num(l.thickness, 3));
      if (l.beyond !== DEFAULT_BEYOND) q.set('beyond', l.beyond);
    }
    // The strip's chart track (`chart=centiles`) and its metric (`metric=wmv`) where the chart is on and the metric isn't GMV.
    if (l.chart === 'centiles') {
      q.set('chart', 'centiles');
      if (l.metric !== DEFAULT_METRIC) q.set('metric', metricParam(l.metric));
    }
    // Parkinson's stages, in that view: the track (`pdtrack=model`) and the ordinal stage (`stage=2.5`) where they aren't its defaults.
    if (s.preset === PD_VIEW) {
      if (l.pdTrack !== DEFAULT_PD_TRACK) q.set('pdtrack', l.pdTrack);
      if (l.stage !== DEFAULT_STAGE) q.set('stage', num(l.stage, 3));
    }
    // Cortical thickness, in that view: the age (`age=40`) or the two ages compared (`ages=20,80`), and the sex (`sex=male`),
    // where they aren't its defaults. Years since birth, the view's own: never the scene's `t`.
    if (s.preset === THICKNESS_VIEW) {
      if (l.thMode === 'compare') q.set('ages', `${l.thAge},${l.thAgeB}`);
      else if (l.thAge !== DEFAULT_TH_AGE) q.set('age', String(l.thAge));
      if (l.thSex !== DEFAULT_TH_SEX) q.set('sex', l.thSex.toLowerCase());
    }
  }
  return q.toString();
}

/** `cam=yaw,pitch,distance,x,y,z`: the orbit as the engine reads it, to a tenth of a degree and a tenth of a millimetre. */
export function encodeOrbit(o: Orbit): string {
  return [o.yawDeg, o.pitchDeg, o.distanceMm, ...o.targetMm].map((v) => num(v, 1)).join(',');
}

export function decodeOrbit(v: string | null): Orbit | null {
  const n = (v ?? '').split(',').map(Number);
  if (n.length !== 6 || n.some((x) => !Number.isFinite(x)) || !(n[2]! > 0)) return null;
  return { yawDeg: n[0]!, pitchDeg: n[1]!, distanceMm: n[2]!, targetMm: [n[3]!, n[4]!, n[5]!] };
}

/** Whether a number in the query is one, and in range. */
const inRange = (v: string | null, lo: number, hi: number): number | null => {
  if (v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= lo && n <= hi ? n : null;
};

/**
 * Apply a query to the store. Unknown values are ignored, so an old link still opens. The Dose
 * waits for the course to load (the store resets the dose when a drug arrives), and the camera for
 * the pack (the bridge sends the view's own camera when a load replies).
 */
export function fromQuery(search: string) {
  const q = new URLSearchParams(search);
  const s = useAtlas.getState();
  const scene = q.get('scene');
  if (scene && SCENARIOS.some((x) => x.id === scene)) s.setScenario(scene as ScenarioId);
  const sc = scenarioById(useAtlas.getState().scenario);
  for (const c of linkChoices(sc)) {
    const v = q.get(c.id);
    if (v) s.setChoice(c.id, v);
  }
  // A view by its id, or a journey view by the journey's (`journey=powers-of-ten`).
  const journey = q.get('journey');
  const view = sc.presets.find((p) => p.id === q.get('view')) ?? (journey ? sc.presets.find((p) => p.journey?.id === journey) : undefined);
  if (view) s.applyPreset(view);
  const iso = decodeTarget(q.get('isolate'));
  if (iso) s.isolateTarget(iso);
  const sec = q.get('section')?.split(':');
  if (sec && AXES.has(sec[0]!) && Number.isFinite(Number(sec[1]))) s.setSection([{ axis: sec[0] as SectionPlane['axis'], mm: Number(sec[1]), keep: 'negative' }]);
  for (const part of (q.get('layers') ?? '').split(',').filter(Boolean)) {
    const [sys, mode] = part.split(':');
    if (sys && MODES.has(mode as Mode)) s.setLayer(sys, mode as Mode);
  }
  for (const id of (q.get('hide') ?? '').split(',').filter(Boolean)) s.setOverride(id, 'hidden');
  for (const id of (q.get('ghost') ?? '').split(',').filter(Boolean)) s.setOverride(id, 'ghost');
  const sel = q.get('select');
  if (sel) s.select(sel);
  const t = Number(q.get('t'));
  const linkTime = Number.isFinite(t) && t > 0 ? t : null;
  if (linkTime !== null) s.setTime(linkTime);
  const speed = inRange(q.get('speed'), 1e-6, 1e6);
  if (speed !== null) s.setSpeed(speed);
  const linkPlay = q.get('play') === '1';
  if (linkPlay) s.setPlaying(true);
  whenSceneSettles(linkTime, linkPlay);
  if (sc.live) {
    const clot = q.get('clot');
    if (clot === 'none') s.setOcclusion(null);
    else if (clot) {
      const at = clot.lastIndexOf('@');
      const vessel = at > 0 ? clot.slice(0, at) : clot;
      const pos = at > 0 ? inRange(clot.slice(at + 1), 0, 1) : null;
      if (vessel) s.setOcclusion(pos === null ? { vessel } : { vessel, s: pos });
    }
    const collaterals = q.get('collaterals');
    if (collaterals === '0') s.setCollaterals(false);
    else if (collaterals === 'fair') s.setCollateralGrade('intermediate');
    if (q.get('pcom') === 'traced') s.setPcom('traced');
    const h = inRange(q.get('thrombectomy'), 1, 6);
    if (h !== null && THROMBECTOMY_H.has(h)) s.setThrombectomy(h);
    if (q.get('compare') === '1') s.setCompare(true);
    const colour = q.get('colour');
    if (colour && COLOURS.has(colour as StrokeState['colourBy'])) s.setColourBy(colour as StrokeState['colourBy']);
    const particles = q.get('particles');
    if (particles === 'off') s.setParticles({ on: false });
    else {
      const density = inRange(particles, 0.05, 1);
      if (density !== null) s.setParticles({ on: true, density });
    }
  }
  const dose = inRange(q.get('dose'), 1e-9, 1e9);
  if (dose !== null) whenDrugLoads(dose);
  const target = q.get('target');
  if (target) s.setTraceView({ target });
  const show = q.get('show');
  useDrugDensity.getState().setShow(isShow(show) ? show : 'engagement');
  const montage = q.get('montage');
  if (montage && MONTAGES.has(montage)) s.setTraceView({ montage: montage as 'eeg' | 'seeg' });
  for (const k of ['eeg', 'seeg', 'activity', 'contacts'] as const) {
    const v = q.get(k);
    if (v === '1' || v === '0') s.setToggle(k, v === '1');
  }
  if (q.get('labels') === '0' && useAtlas.getState().labelsOn) s.toggleLabels();
  const density = q.get('density');
  if (density && DENSITIES.has(density as LabelDensity)) s.setLabelDensity(density as LabelDensity);
  const explode = inRange(q.get('explode'), 0, 1);
  if (explode !== null) s.setExplode(explode);
  if (q.get('traces') === '0') s.setTracesOpen(false);
  if (q.get('present') === '1') s.setPresenting(true);
  const gallery = q.get('gallery');
  if (gallery) s.setGallery(true, gallery === '1' ? null : gallery);
  // The tour and its step. A link that carries a scene state positions the player over it (the bar writes both). A bare link,
  // `?tour=start.2` with no state beside it, opens the step's own state through tourTo, as Next would (the lead, 10-03).
  const tour = decodeTour(q.get('tour'));
  if (tour && [...q.keys()].every((k) => BARE_TOUR_KEYS.has(k))) void tourTo(tour.id, tour.step);
  else if (tour) s.setTour({ ...tour, paused: false });
  const intensity = inRange(q.get('intensity'), INTENSITY.min, INTENSITY.max);
  if (intensity !== null) useStim.getState().setIntensity(intensity);
  const current = inRange(q.get('current'), DEFAULT_CURRENT.min, DEFAULT_CURRENT.max);
  if (current !== null) useStim.getState().setCurrent(current);
  const coil = q.get('coil')?.split(',');
  if (coil?.length === 2 && coil.every((v) => v.trim() !== '' && Number.isFinite(Number(v)))) useStim.getState().setOffset([Number(coil[0]), Number(coil[1])]);
  if (q.get('source') === '1') useStim.getState().setSourceModel(true);
  const condition = q.get('condition');
  if (condition && /^[a-z0-9-]+$/.test(condition)) useDfc.getState().setCondition(condition);
  if (q.get('matrix') === '1') useDfc.getState().setMatrixOpen(true);
  if (q.get('compare') === '1') useCompare.getState().setOpen(true);
  if (q.get('maps') === '1') useDrugMaps.getState().setOpen(true);
  const topic = q.get('q');
  if (topic !== null) useText.getState().submit(topic);
  if (q.get('live') === 'paint') requestSource('paint');
  const wall = q.get('wall');
  if (isWall(wall)) useCfd.getState().setWall(wall);
  const tracers = q.get('tracers');
  if (tracers === '0') useCfd.getState().setTracersOn(false);
  else if (tracers !== null && /^\d+$/.test(tracers)) useCfd.getState().setTracers(Number(tracers));
  const map = q.get('map');
  if (isMap(map)) useLifetime.getState().setMap(map);
  const track = q.get('track');
  if (isTrack(track)) useLifetime.getState().setTrack(track);
  // Assigned thickness (the track's own growth axis, never `t`): `g=`, `h=` and `beyond=last`.
  const growth = inRange(q.get('g'), GROWTH_RANGE[0], GROWTH_RANGE[1]);
  if (growth !== null) useLifetime.getState().setGrowth(growth);
  const thickness = q.get('h');
  if (thickness !== null && thickness !== '' && Number.isFinite(Number(thickness)) && Number(thickness) > 0) useLifetime.getState().setThickness(Number(thickness));
  const beyond = q.get('beyond');
  if (isBeyond(beyond)) useLifetime.getState().setBeyond(beyond);
  const pdTrack = q.get('pdtrack');
  if (isPdTrack(pdTrack)) useLifetime.getState().setPdTrack(pdTrack);
  if (q.get('chart') === 'centiles') useLifetime.getState().setChart('centiles');
  const metric = metricFromParam(q.get('metric'));
  if (metric) useLifetime.getState().setMetric(metric);
  const stage = inRange(q.get('stage'), STAGE_RANGE[0], STAGE_RANGE[1]);
  if (stage !== null) useLifetime.getState().setStage(stage);
  // Cortical thickness, in that view (the view's own ages, years since birth): `age=`, `ages=A,B` for two ages compared, and `sex=`.
  if (useAtlas.getState().preset === THICKNESS_VIEW) {
    const l = useLifetime.getState();
    const age = inRange(q.get('age'), THICKNESS_AGES[0], THICKNESS_AGES[1]);
    if (age !== null) l.setThAge(age);
    const ages = (q.get('ages') ?? '').split(',');
    if (ages.length === 2) {
      const a = inRange(ages[0]!, THICKNESS_AGES[0], THICKNESS_AGES[1]);
      const b = inRange(ages[1]!, THICKNESS_AGES[0], THICKNESS_AGES[1]);
      if (a !== null && b !== null) {
        l.setThAge(a);
        l.setThAgeB(b);
        l.setThMode('compare');
      }
    }
    const sex = q.get('sex');
    const cased = sex ? `${sex[0]!.toUpperCase()}${sex.slice(1).toLowerCase()}` : null;
    if (isSex(cased)) l.setThSex(cased);
  }
  const cam = decodeOrbit(q.get('cam'));
  // On a journey view the camera is the path's: the journey opens stopped at the link's camera instead (features/journey.ts).
  if (cam && journeyOf(useAtlas.getState())) useJourney.getState().setOpenCamera(cam);
  else if (cam) whenPackLoads(cam);
}

/** The Dose slider's mass, once the scene's course has loaded: the store resets the dose to the course's own when a drug arrives. */
function whenDrugLoads(mass: number) {
  const un = useAtlas.subscribe(
    (s) => s.drug,
    (d) => {
      if (!d) return;
      un();
      useAtlas.getState().setDrugDose(mass);
    },
  );
}

/** Whether the scene has what it draws: its pack, and its sims where it has any (Neurons' close-up frames its cells once they're in). */
export const sceneSettled = (s: Pick<AtlasState, 'scenario' | 'load' | 'sims'>): boolean =>
  s.load.status === 'ready' && (!scenarioById(s.scenario).sims?.length || (s.sims.scenario === s.scenario && (s.sims.status === 'ready' || s.sims.status === 'error')));

/**
 * The link's time and play state again, once the scene's run has given the clock. They are set at once, for the scene
 * as it is; but until its main run loads the scene keeps its placeholder timeline (Neurons' 1.15 ms, against a column
 * played over 7 s), the sims' fit clamps a later time to that end, and a clock that reaches it stops. So a link into
 * a run (`?scene=neurons&run=…&t=2&play=1`, the column's exact state) sets both again after the sims settle, the time
 * kept inside the run's own timeline. Nothing is waited for where the scene has no sims or has settled already, and
 * nothing lands on a scene the person moved to meanwhile.
 */
function whenSceneSettles(t: number | null, play: boolean) {
  if (t === null && !play) return;
  const s = useAtlas.getState();
  const scene = s.scenario;
  if (!scenarioById(scene).sims?.length || sceneSettled(s)) return;
  const un = useAtlas.subscribe(sceneSettled, (settled) => {
    if (!settled) return;
    un();
    // The sims' own fit of the clock runs with these state changes; the link's state lands after it.
    setTimeout(() => {
      const st = useAtlas.getState();
      if (st.scenario !== scene) return;
      const tl = sceneTimelineOf(st);
      if (t !== null) st.setTime(tl ? Math.min(Math.max(t, tl.start ?? 0), tl.duration) : t);
      if (play) st.setPlaying(true);
    }, 0);
  });
}

/** The URL's camera, once the pack's load has replied and the bridge has sent the view's own: a cut, not a flight. */
function whenPackLoads(orbit: Orbit) {
  const un = useAtlas.subscribe(sceneSettled, (settled) => {
    if (!settled) return;
    un();
    // The bridge's syncAll and the sims' own framing run after these state changes; the cut lands after them.
    setTimeout(() => useAtlas.getState().camera({ orbit, transitionMs: 0 }), 0);
  });
}

/**
 * A link to the view as it is: the bar's state with the camera as the engine last reported it, and
 * without developer switches. `base` is the page without its query.
 */
export function shareUrl(s: UrlState = useAtlas.getState(), orbit: Orbit | null | undefined = lastCamera.current?.orbit, base = `${location.origin}${location.pathname}`): string {
  const q = new URLSearchParams(toQuery(s));
  if (orbit) q.set('cam', encodeOrbit(orbit));
  const str = q.toString();
  return str ? `${base}?${str}` : base;
}

export function syncUrl() {
  let last = '';
  let timer = 0;
  const write = () => {
    const s = useAtlas.getState();
    const keep = new URLSearchParams(location.search);
    const next = new URLSearchParams(toQuery(s));
    // Developer switches (engine=mock, stats) survive.
    for (const k of DEV_PARAMS) {
      const v = keep.get(k);
      if (v !== null) next.set(k, v);
    }
    const str = next.toString();
    if (str === last) return;
    last = str;
    history.replaceState(null, '', str ? `?${str}` : location.pathname);
  };
  const schedule = () => {
    // Time changes every frame while playing: write at most four times a second. A pending write is left to
    // fire, and reads the state then, so the bar follows a playing scene (a tour step) instead of waiting for
    // it to stop, and the last change is written within 250 ms.
    if (timer) return;
    timer = window.setTimeout(() => {
      timer = 0;
      write();
    }, 250);
  };
  useAtlas.subscribe((s) => toQuery(s), schedule);
  useStim.subscribe(schedule);
  useDfc.subscribe(schedule);
  useCompare.subscribe(schedule);
  useDrugMaps.subscribe(schedule);
  useMindLive.subscribe(schedule);
  useText.subscribe(schedule);
  useCfd.subscribe(schedule);
  useLifetime.subscribe(schedule);
}
